import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// number helper
const toNum = (v, d = 0) => {
  const n = typeof v === "string" ? v.replace(/[, ]+/g, "") : v;
  const f = Number(n);
  return Number.isFinite(f) ? f : d;
};

export default function UnitPricingEstimateEdit() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState([]);

  // header
  const [hdr, setHdr] = useState({
    company: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    company_address: "",
    date: new Date().toISOString().slice(0,10),
    subtotal: 0,
    tax_rate: 0,
    tax: 0,
    total: 0,
    notes: "",
  });
  const handleHdr = (k, v) => setHdr((p) => ({ ...p, [k]: v }));

  // items
  const [items, setItems] = useState([]); // [{id, device, cat, sku, manufacturer, description, unit_price, quantity, line_total, unit_item_id?}]

  // For adding new line via catalog
  const [selectedCat, setSelectedCat] = useState("-");
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [qtyInput, setQtyInput] = useState("1");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // load estimate header + items
        const { data: detail } = await axios.get(`${apiUrl}/unit_pricing_crud.php?estimate_detail=${id}`, {
          withCredentials: true,
        });
        if (!detail?.header) {
          toast.error("Estimate not found", { position: "top-center" });
          navigate(-1);
          return;
        }

        const h = detail.header;
        setHdr({
          company: h.company || "",
          contact_name: h.contact_name || "",
          contact_email: h.contact_email || "",
          contact_phone: h.contact_phone || "",
          company_address: h.company_address || "",
          date: h.date || new Date().toISOString().slice(0,10),
          subtotal: toNum(h.subtotal, 0),
          tax_rate: toNum(h.tax_rate, 0),
          tax: toNum(h.tax, 0),
          total: toNum(h.total, 0),
          notes: h.notes || "",
        });

        const normalizedItems = (detail.items || []).map((r) => ({
          id: r.id, // item row id
          unit_item_id: r.unit_item_id ?? null,
          device: r.device,
          description: r.description,
          cat: r.cat,
          sku: r.sku,
          manufacturer: r.manufacturer,
          unit_price: toNum(r.unit_price, 0),
          quantity: toNum(r.quantity, 0),
          line_total: toNum(r.line_total, 0),
        }));
        setItems(normalizedItems);

        // load catalog for adding new
        const { data: cat } = await axios.get(`${apiUrl}/unit_pricing_crud.php?catalog=1`, {
          withCredentials: true,
        });
        const normalizedCatalog = Array.isArray(cat.items)
          ? cat.items.map((r, i) => ({
              id: r.id ?? i + 1,
              cat: String(r.cat || "").trim(),
              device: String(r.device || r.item || "").trim(),
              description: String(r.description || "").trim(),
              unit_price: toNum(r.unit_price ?? r.price ?? 0),
              sku: String(r.sku || "").trim(),
              manufacturer: String(r.manufacturer || "").trim(),
              notes: String(r.notes || "").trim(),
            }))
          : [];
        setCatalog(normalizedCatalog);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load estimate", { position: "top-center" });
      } finally {
        setLoading(false);
      }
    })();
  }, [apiUrl, id, navigate]);

  // derived totals from current items + hdr.tax_rate
  const subTotal = useMemo(
    () => +items.reduce((s, li) => s + toNum(li.line_total, 0), 0).toFixed(2),
    [items]
  );
  const tax = useMemo(
    () => +((subTotal * toNum(hdr.tax_rate, 0)) / 100).toFixed(2),
    [subTotal, hdr.tax_rate]
  );
  const grandTotal = useMemo(() => +(subTotal + tax).toFixed(2), [subTotal, tax]);

  // categories / device options for adding
  const categories = useMemo(() => {
    const set = new Set();
    catalog.forEach((r) => r.cat && set.add(r.cat));
    return ["-", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [catalog]);

  const deviceOptions = useMemo(() => {
    const rows = selectedCat === "ALL" ? catalog : catalog.filter((r) => r.cat === selectedCat);
    return rows.sort((a, b) => a.device.localeCompare(b.device));
  }, [catalog, selectedCat]);

  // item update helpers
  const setItemField = (rowId, key, value) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== rowId) return it;
        const upd = { ...it, [key]: value };
        const qty = toNum(key === "quantity" ? value : upd.quantity, 0);
        const unit = toNum(key === "unit_price" ? value : upd.unit_price, 0);
        upd.line_total = +(qty * unit).toFixed(2);
        return upd;
      })
    );
  };

  const removeItem = (rowId) => setItems((prev) => prev.filter((it) => it.id !== rowId));

  const addNewItemFromCatalog = () => {
    if (!selectedDeviceId) {
      toast.error("Pick a device first.", { position: "top-center" });
      return;
    }
    const base = catalog.find((r) => String(r.id) === String(selectedDeviceId));
    if (!base) {
      toast.error("Device not found.", { position: "top-center" });
      return;
    }
    const qty = Math.max(0, parseInt(qtyInput, 10) || 0);
    if (qty <= 0) {
      toast.error("Quantity must be greater than 0.", { position: "top-center" });
      return;
    }

    // Give new item a client-only temp id (negative) to keep keys unique
    const tempId = -Math.floor(Math.random() * 1e9);

    setItems((prev) => [
      ...prev,
      {
        id: tempId,
        unit_item_id: base.id,
        device: base.device,
        description: base.description,
        cat: base.cat,
        sku: base.sku,
        manufacturer: base.manufacturer,
        unit_price: toNum(base.unit_price, 0),
        quantity: qty,
        line_total: +(qty * toNum(base.unit_price, 0)).toFixed(2),
      },
    ]);

    setSelectedDeviceId("");
    setQtyInput("1");
  };

  const handleSave = async () => {
    if (!hdr.company || !hdr.contact_email) {
      toast.error("Company and Contact Email are required.", { position: "top-center" });
      return;
    }
    if (items.length === 0) {
      toast.error("Add at least one item.", { position: "top-center" });
      return;
    }

    const payload = {
      status: "update_estimate",
      estimate_id: Number(id),
      header: {
        ...hdr,
        subtotal: subTotal,
        tax_rate: toNum(hdr.tax_rate, 0),
        tax,
        total: grandTotal,
      },
      items: items.map((p) => ({
        // do not send the row's own id for upsert simplicity; backend will replace items set
        unit_item_id: p.unit_item_id ?? null,
        device: p.device,
        description: p.description,
        cat: p.cat,
        sku: p.sku,
        manufacturer: p.manufacturer,
        unit_price: toNum(p.unit_price, 0),
        quantity: toNum(p.quantity, 0),
        line_total: toNum(p.line_total, 0),
      })),
    };

    try {
      const response = await axios.post(`${apiUrl}/unit_pricing_crud.php`, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      if (response.data?.success) {
        toast.success("Estimate updated!", { position: "top-center" });
        navigate("/unit_pricing_estimate_list");
      } else {
        toast.error(response.data?.error || "Update failed", { position: "top-center" });
      }
    } catch (e) {
      console.error(e);
      toast.error("Update failed", { position: "top-center" });
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] p-4">
      <h1 className="text-2xl font-bold mb-4">Edit Estimate #{id}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Header + Add-from-catalog */}
        <div className="border rounded-lg p-4 bg-white">
          {/* Header 3-up layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <input className="border rounded px-3 py-2" placeholder="Company" value={hdr.company} onChange={(e)=>handleHdr('company', e.target.value)} />
            <input className="border rounded px-3 py-2" placeholder="Contact Name" value={hdr.contact_name} onChange={(e)=>handleHdr('contact_name', e.target.value)} />
            <input className="border rounded px-3 py-2" placeholder="Contact Email" value={hdr.contact_email} onChange={(e)=>handleHdr('contact_email', e.target.value)} />
            <input className="border rounded px-3 py-2" placeholder="Contact Phone" value={hdr.contact_phone} onChange={(e)=>handleHdr('contact_phone', e.target.value)} />
            <input className="border rounded px-3 py-2" placeholder="Company Address" value={hdr.company_address} onChange={(e)=>handleHdr('company_address', e.target.value)} />
            <input type="date" className="border rounded px-3 py-2" value={hdr.date} onChange={(e)=>handleHdr('date', e.target.value)} />
          </div>

          {/* Tax */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Tax %</label>
              <input type="number" min={0} step="0.001" className="border rounded px-3 py-2 w-full"
                value={hdr.tax_rate} onChange={(e)=>handleHdr('tax_rate', e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Proposal Notes</label>
              <textarea rows={3} className="border rounded px-3 py-2 w-full"
                value={hdr.notes} onChange={(e)=>handleHdr('notes', e.target.value)} />
            </div>
          </div>

          {/* Add from catalog */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
              <select className="border rounded px-3 py-2 w-full" value={selectedCat}
                onChange={(e)=>{ setSelectedCat(e.target.value); setSelectedDeviceId(""); }}>
                {categories.map((c)=> <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Device</label>
              <select className="border rounded px-3 py-2 w-full" value={selectedDeviceId}
                onChange={(e)=>setSelectedDeviceId(e.target.value)} disabled={deviceOptions.length===0}>
                <option value="">Select a device…</option>
                {deviceOptions.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.device} {d.sku ? `(${d.sku})` : ""} — ${toNum(d.unit_price,0).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Qty</label>
              <input type="number" min={1} value={qtyInput} onChange={(e)=>setQtyInput(e.target.value)}
                className="border rounded px-3 py-2 w-full text-right" />
            </div>
          </div>

          <button
            onClick={addNewItemFromCatalog}
            className="bg-emerald-600 text-white px-3 py-2 rounded shadow hover:bg-emerald-700 disabled:opacity-50"
            disabled={!selectedDeviceId}
          >
            Add Item
          </button>
        </div>

        {/* RIGHT: Items grid + totals + save */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr className="text-left">
                  <th className="px-3 py-2">Device / Description</th>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2 text-right">Unit</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Line</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.device}</div>
                      {p.description ? <div className="text-gray-500">{p.description}</div> : null}
                    </td>
                    <td className="px-3 py-2">{p.sku}</td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" step="0.01" min={0}
                        value={p.unit_price}
                        onChange={(e)=>setItemField(p.id, 'unit_price', e.target.value)}
                        className="w-24 text-right border rounded px-2 py-1" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" min={0}
                        value={p.quantity}
                        onChange={(e)=>setItemField(p.id, 'quantity', e.target.value)}
                        className="w-20 text-right border rounded px-2 py-1" />
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">
                      ${toNum(p.line_total, 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={()=>removeItem(p.id)} className="text-red-600 hover:text-red-800">&times;</button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                      {loading ? "Loading…" : "No items yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 border rounded p-4 bg-gray-50">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold">${subTotal.toFixed(2)}</span>
            </div> 
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Tax ({toNum(hdr.tax_rate,0).toFixed(3)}%)</span>
              <span className="font-semibold">${tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg">
              <span className="font-bold">Total</span>
              <span className="font-extrabold">${grandTotal.toFixed(2)}</span>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button onClick={handleSave} className="bg-blue-600 text-white px-3 py-2 rounded shadow hover:bg-blue-700">
              Save Changes
            </button>
            <button onClick={()=>navigate(-1)} className="bg-gray-100 px-3 py-2 rounded hover:bg-gray-200">
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
